/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
/**
 * Useful constants that are used throughout the app.
 */
const constants = {

    /** Version of the app. */
    appVersion: require('../../package.json').version,

    /** Render Layers */
    layers: {
        render: 0,
        objectCollision: 1,
        avatarCollision: 2,
        targetable: 3,
    },

    /** Common colors associated with the app. */
    colors: {
        blue: '#0074cc',
        lightBlue: '#2ea7ff',
        orange: '#e85b2d',
        darkOrange: '#d65500',
        darkGrey: '#7b8082',
        green: '#2dca8c',
        indigo: '#1b1464',
        lightGrey: '#aaaaaa',
        midGrey: '#343e40',
        red: '#fa5252',
        black: '#202020',
        yellow: '#ffc700',
        linkBlue: '#58a6ff',
        white: '#FFFFFF',
    },

    /** List of registered domains */
    domains: {
        /** Base domain of the Spaces platform */
        base: 'https://spaces.vatom.com',

        /** Production host name */
        prod: 'spaces.vatom.com',

        /** Development host name */
        dev: 'spaces.vatominc.cool',
        devOld: 'spatialweb.cool',

        /** Testing host name */
        test: 'spaces.vatominc.cc',
        testOld: 'spatialweb.cc'
    },

    /** URLs for help sections */
    helpURLs: {
        /** General Spaces help */
        general: 'https://support.vatom.com/hc/en-us/categories/8125554876955-Spaces',

        /** Help with sound, microphone and camera */
        audioVideo: 'https://support.vatom.com/hc/en-us/articles/8126187842203-Why-isn-t-my-sound-microphone-camera-working-',

        /** Help with object collision */
        collision: 'https://support.vatom.com/hc/en-us/articles/8126273411355-Collision-in-Vatom-Spaces'
    },

    /** Default avatar speeds */
    speeds: {
        /** Default avatar walking speed */
        walk: 2,

        /** Default avatar sprinting speed */
        sprint: 4
    },

    /** Default view values */
    view: {
        /** Default orientation */
        orientation: 0,

        /** Default zoom level */
        zoom: 7.25,

        /** Default tilt angle (in degrees) */
        tilt: 77,

        /** Default minimum zoom level */
        minZoom: 7,

        /** Default maximum zoom level */
        maxZoom: 40
    },

    /** Our core user view modes */
    coreViews: ['swivel', 'topdown', 'first-person'],

    /** Height (in pixels) of the bottom HUD control bar. */
    controlBarHeight: 58,

    /** Height (in pixels) of the top EditorUI menu bar. */
    editorBarHeight: 36,

    /** Height (in pixels) used for the `PanelHeader` and `AccordionHeader` components. */
    panelHeaderHeight: 48,

    /** Width of the "Plugins" panel */
    pluginPanelWidth: 380,

    /** Maximum image size (in megabytes) allowed to be uploaded. */
    imageSizeLimit: 2,

    /** Maximum file size (in bytes) allowed to be uploaded. */
    fileSizeLimit: 10 * 1024 * 1024,

    /** Number of decibels above which a user is deemed to be speaking. */
    speakingCutoff: -47,

    /** Prefix used to identify map items in the "Scene" tab of the Editor. */
    sceneItemPrefix: 'scene-',

    /** Transfer type of a map item. Used for dragging and dropping map items. */
    mapItemTransferType: 'application/x-spatialweb-mapitem-id',

    /** Default font to use. */
    fontFamily: 'Inter, Verdana, Arial',

    /** URL of the Vatom Terms of Service. */
    termsOfServiceURL: 'https://www.vatom.com/terms-of-service/',

    /** URL of the Vatom Privacy Policy. */
    privacyPolicyURL: 'https://www.vatom.com/privacy-policy/',

    /** Options related to controlling the frame rate. */
    frameRateOptions: {
        items: ['Automatic', '90', '72', '60', '45', '30'],
        values: [0, 90 + 3, 72 + 3, 60 + 3, 45 + 3, 30 + 3]
    },

    /** Options related to controlling the pixel density. */
    pixelDensityOptions: {
        items: ['Automatic', 'Best', '0.5x', '0.75x', '0.8x', '0.9x', '1x', '1.25x', '1.5x', '2x', '2.5x', '3x'],
        values: [-1, 0, 0.5, 0.75, 0.8, 0.9, 1.0, 1.25, 1.5, 2, 2.5, 3]
    },

    /** Properties for each type of popup. */
    popupProps: {
        yesNo: {
            showCancelButton: true,
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            confirmButtonColor: '#2dca8c',
            cancelButtonColor: '#fa5252'
        },

        okCancel: {
            showCancelButton: true,
            confirmButtonText: 'OK',
            cancelButtonText: 'Cancel',
        }
    },

    /**
     * Width (in pixels) where if the device width is below this value, it is
     * considered to be a mobile device and if the width is above (or equal to)
     * this value, it is considered to be a non-mobile device.
     */
    mobileBreakpointWidth: 750,

    /** GetStream API key */
    chatAPIKey: 'trc9t7bpcu8s',

    /** All Vatom logos */
    vatomLogos: {
        /** Base logos for Vatom */
        base: {
            /** Color logo for Vatom */
            color: require('../images/icons/vatom-logo.svg'),

            /** White logo for Vatom */
            white: require('../images/icons/vatom-logo-white.svg')
        },

        /** Logo for Vatom Developer Portal */
        devPortal: require('../images/icons/vatom-dev-portal-logo.svg'),

        /** Vatom Spaces logos */
        spaces: {
            /** Vatom Spaces logo with black text */
            black: require('../images/icons/vatom-spaces-logo.svg'),

            /** Vatom Spaces logo with white text */
            white: require('../images/icons/vatom-spaces-logo-white.svg')
        },

        /** Horizontal Vatom logos */
        horizontal: {
            /** Horizontal Vatom logo with black text */
            black: require('../images/icons/vatom-horizontal.svg'),

            /** Horizontal Vatom logo with white text */
            white: require('../images/icons/vatom-horizontal-white.svg')
        },

        /** Vertical Vatom logos */
        vertical: {
            /** Vertical Vatom logo with black text */
            black: require('../images/icons/vatom-logo-vertical.svg'),

            /** Vertical Vatom logo with white text */
            white: require('../images/icons/vatom-logo-vertical-white.svg')
        }
    },

    /**
     * `true` if we should show the mobile UI, `false` otherwise.
     * Note that this can change while the app is running if the browser is resized.
     */
    get isMobile() {
        return window.innerWidth < constants.mobileBreakpointWidth
    },

    /** @type {number} Height (in pixels) used for the `DeveloperBanner` component */
    get developerBannerHeight() {
        return this.isMobile ? 43 : 60
    },

    /** Studio base URLs */
    studioURL: {
        prod: "https://studio.api.vatominc.com",
        dev: "https://studio.api.vatominc.cool",
    },

    /** Algolia API Key and App ID */
    algolia: {
        appId: {
            dev: "TR8084ALHK",
            prod: "ACU50L56PA"
        },
        key: {
            dev: "5eb165b9a4c367038d839d56a23a762e",
            prod: "f2976ea14c31b6777243418e34ea1e6a"
        }
    },

    /** Peer-to-peer server config. */
    iceServers: [
        // Public servers
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:global.stun.twilio.com:3478' },

        // Private Xirsys server
        { urls: 'stun:ws-turn4.xirsys.com' },
        {
            urls: [
                'turn:ws-turn4.xirsys.com:80?transport=udp',
                'turn:ws-turn4.xirsys.com:3478?transport=udp',
                'turn:ws-turn4.xirsys.com:80?transport=tcp',
                'turn:ws-turn4.xirsys.com:3478?transport=tcp',
                'turns:ws-turn4.xirsys.com:443?transport=tcp',
                'turns:ws-turn4.xirsys.com:5349?transport=tcp',
            ],
            username: 'ByFitRqfy1ibiEQyY9A3-uOkzb0FD1Ygo31Q9wPF5iMjY58VH_N1QqlMg4DFNr7pAAAAAGM_GF12YXRvbWluYw==',
            credential: '23099f72-45a1-11ed-8259-0242ac140004',
        },
    ]

}

export default constants
